import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Route, Boxes } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ENTITY_LABELS, ENTITY_ORDER } from "@/lib/entityColors";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { EntryComposer } from "@/components/journey/EntryComposer";

// Attach a typed note to either a whole path or any specific entity in the graph.
export const AttachNoteDialog = ({ open, onOpenChange, presetNode }) => {
  const [browse, setBrowse] = useState(null);
  const [mode, setMode] = useState("entity"); // "path" | "entity"
  const [trackId, setTrackId] = useState("");
  const [node, setNode] = useState(null); // {id, label}
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(presetNode ? "entity" : "path");
    setNode(presetNode || null);
    setTrackId("");
    if (!browse) api.get("/kg/browse").then((res) => setBrowse(res.data)).catch(() => {});
  }, [open, presetNode]); // eslint-disable-line react-hooks/exhaustive-deps

  const paths = (browse || []).find((g) => g.type === "knowledge_system")?.nodes || [];
  const allNodes = (browse || []).flatMap((g) => g.nodes.map((n) => ({ ...n, type: g.type })));

  const contextLabel = mode === "entity"
    ? (node?.label || "select an entity")
    : (paths.find((p) => p.id === trackId)?.label || "select a path");

  const addEntry = async (payload) => {
    if (mode === "entity" && !node) { toast.error("Pick an entity first"); return; }
    if (mode === "path" && !trackId) { toast.error("Pick a path first"); return; }
    try {
      await api.post("/notes", {
        ...payload,
        node_id: mode === "entity" ? node.id : null,
        track_id: mode === "path" ? trackId : null,
      });
      toast.success("Saved to your journal");
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save. Please sign in."); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="attach-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Add a note</DialogTitle>
          <DialogDescription>Choose where to file it, then jot a note, link, quote or question.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setMode("path")} data-testid="attach-mode-path"
              className={`flex items-center justify-center gap-2 text-sm rounded-md border py-2 transition-colors ${
                mode === "path" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
              }`}>
              <Route className="w-4 h-4" /> A whole path
            </button>
            <button onClick={() => setMode("entity")} data-testid="attach-mode-entity"
              className={`flex items-center justify-center gap-2 text-sm rounded-md border py-2 transition-colors ${
                mode === "entity" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"
              }`}>
              <Boxes className="w-4 h-4" /> A specific entity
            </button>
          </div>

          {mode === "path" ? (
            <Select value={trackId} onValueChange={setTrackId}>
              <SelectTrigger data-testid="attach-path-select"><SelectValue placeholder="Select a path…" /></SelectTrigger>
              <SelectContent>
                {paths.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Popover open={picker} onOpenChange={setPicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="attach-entity-trigger">
                  <span className="truncate">{node ? node.label : "Search any entity…"}</span>
                  <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search entities…" data-testid="attach-entity-search" />
                  <CommandList>
                    <CommandEmpty>No entity found.</CommandEmpty>
                    {ENTITY_ORDER.map((t) => {
                      const items = allNodes.filter((n) => n.type === t);
                      if (items.length === 0) return null;
                      return (
                        <CommandGroup key={t} heading={ENTITY_LABELS[t] || t}>
                          {items.map((n) => (
                            <CommandItem key={n.id} value={`${n.label} ${n.id}`}
                              onSelect={() => { setNode({ id: n.id, label: n.label }); setPicker(false); }}
                              data-testid={`attach-entity-${n.id}`}>
                              <Check className={`w-4 h-4 mr-2 ${node?.id === n.id ? "opacity-100" : "opacity-0"}`} />
                              {n.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          <EntryComposer target={{ title: contextLabel }} allowClear={false} onAdd={addEntry} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
